
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { acceptInvitation } from '@/services/invitationService';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { Icons } from '@/components/icons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useEffect as useReactEffect } from 'react';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';
import { BackgroundEffects } from '@/components/landing/BackgroundEffects';
import { SonarAnimation } from '@/components/landing/SonarAnimation';

export default function Auth() {
  const navigate = useNavigate();
  const { user, signIn, signUp, isLoading } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>('login');
  const location = useLocation();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [switchedAccount, setSwitchedAccount] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('invite');
    const emailFromLink = params.get('email');
    const accountCreatedParam = params.get('account_created') === 'true';
    const passwordUpdatedParam = params.get('password_updated') === 'true';
    const switchedAccountParam = params.get('switched_account') === 'true';
    
    if (token) {
      setInviteToken(token);
      setActiveTab('login');
    }
    if (emailFromLink) {
      setSignupEmail(emailFromLink);
      setLoginEmail(emailFromLink);
      setInvitedEmail(emailFromLink);
    }
    if (accountCreatedParam) {
      setAccountCreated(true);
      setActiveTab('login');
    }
    if (passwordUpdatedParam) {
      setPasswordUpdated(true);
      setActiveTab('login');
    }
    if (switchedAccountParam) {
      setSwitchedAccount(true);
      setActiveTab('login');
    }
  }, [location]);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupPasswordConfirm, setShowSignupPasswordConfirm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signingUp, setSigningUp] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoggingIn(true);
    
    try {
      await signIn(loginEmail, loginPassword);
      
      // Accept any pending invite token after login
      if (inviteToken) {
        try {
          await acceptInvitation(inviteToken);
          toast({
            title: 'Invitation accepted',
            description: 'You have successfully joined the company.',
          });
          // Navigate to companies page
          navigate('/companies');
          return;
        } catch (e) {
          console.error('Failed to accept invitation:', e);
        }
      }
    } catch (error: any) {
      setLoginError(
        error.message === 'Invalid login credentials'
          ? 'Incorrect email or password'
          : error.message || 'Failed to sign in'
      );
    } finally {
      setLoggingIn(false);
    }
  };
  
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (signupPassword !== signupPasswordConfirm) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure your passwords match",
        variant: "destructive",
      });
      return;
    }
    
    if (signupPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    try {
      setSigningUp(true);
      await signUp(signupEmail, signupPassword, firstName, lastName);
      
      // Clear form on success
      setSignupEmail('');
      setSignupPassword('');
      setSignupPasswordConfirm('');
      setFirstName('');
      setLastName('');
      
      toast({
        title: "Sign up successful",
        description: inviteToken 
          ? "Account created! Please log in to accept your invitation." 
          : "You can now log in with your new account.",
      });
      
      // Switch to login tab so user can sign in and accept invitation
      setActiveTab('login');
      
      // Pre-fill login email
      if (signupEmail) {
        setLoginEmail(signupEmail);
      }
      
    } catch (error) {
      console.error('Error during signup:', error);
      toast({
        title: "Sign up failed",
        description: "An error occurred during sign up.",
        variant: "destructive"
      });
    } finally {
      setSigningUp(false);
    }
  };
  
  const toggleLoginPasswordVisibility = () => {
    setShowLoginPassword(!showLoginPassword);
  };

  const toggleSignupPasswordVisibility = () => {
    setShowSignupPassword(!showSignupPassword);
  };

  const toggleSignupPasswordConfirmVisibility = () => {
    setShowSignupPasswordConfirm(!showSignupPasswordConfirm);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen landing-gradient-bg">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center landing-gradient-bg p-4 relative overflow-hidden">
      {/* Animated background */}
      <SonarAnimation />
      <BackgroundEffects />

      <Card className="w-full max-w-md landing-glass rounded-2xl relative z-10 animate-fade-in">
        <CardHeader className="space-y-3 pb-6">
          <div className="flex flex-col items-center justify-center space-y-2">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[hsl(262,83%,53%)] to-[hsl(187,85%,43%)] flex items-center justify-center shadow-lg landing-shadow-purple">
              <Icons.logo className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-3xl text-center font-bold landing-gradient-text">
              Discovery Hub
            </CardTitle>
          </div>
          <CardDescription className="text-center text-base text-white/60">
            Welcome back! Sign in to continue
          </CardDescription>
          {accountCreated && (
            <Alert className="mt-4 bg-[hsl(262,83%,53%)]/20 border-[hsl(262,83%,53%)]/40 backdrop-blur-sm animate-fade-in">
              <AlertCircle className="h-4 w-4 text-[hsl(262,83%,53%)]" />
              <AlertDescription className="text-sm text-white/90">
                <strong className="text-[hsl(262,83%,53%)]">Account created successfully!</strong>
                <br />
                Check your email to set your password, then log in to access your company.
              </AlertDescription>
            </Alert>
          )}
          {passwordUpdated && (
            <Alert className="mt-4 bg-[hsl(187,85%,43%)]/20 border-[hsl(187,85%,43%)]/40 backdrop-blur-sm animate-fade-in">
              <CheckCircle className="h-4 w-4 text-[hsl(187,85%,43%)]" />
              <AlertDescription className="text-sm text-white/90">
                <strong className="text-[hsl(187,85%,43%)]">Password Updated Successfully!</strong>
                <br />
                You can now log in with your new password.
              </AlertDescription>
            </Alert>
          )}
          {switchedAccount && invitedEmail && (
            <Alert className="mt-4 bg-white/10 border-white/20 backdrop-blur-sm animate-fade-in">
              <CheckCircle className="h-4 w-4 text-white/80" />
              <AlertDescription className="text-sm text-white/90">
                <strong className="text-white">Account Switched</strong>
                <br />
                You've been logged out. Please log in with <strong>{invitedEmail}</strong> to accept the invitation.
              </AlertDescription>
            </Alert>
          )}
          {inviteToken && invitedEmail && !switchedAccount && (
            <Alert className="mt-4 bg-white/10 border-white/20 backdrop-blur-sm animate-fade-in">
              <AlertCircle className="h-4 w-4 text-white/80" />
              <AlertDescription className="text-sm text-white/90">
                <strong className="text-white">Company Invitation</strong>
                <br />
                This invitation was sent to <strong>{invitedEmail}</strong>.
                <br />
                Please log in or create an account with this email address.
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent className="px-6 pb-6">
          <Tabs 
            defaultValue="login" 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 mb-8 h-11 bg-white/5 p-1 border border-white/10 rounded-lg">
              <TabsTrigger 
                value="login" 
                className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50 font-medium rounded-md transition-all"
              >
                Login
              </TabsTrigger>
              <TabsTrigger 
                value="signup" 
                className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50 font-medium rounded-md transition-all"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="animate-fade-in">
              <form onSubmit={handleLogin} className="space-y-5">
                {loginError && (
                  <Alert variant="destructive" className="mb-4 animate-fade-in bg-red-500/20 border-red-500/40">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-white/90">{loginError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2.5">
                  <Label htmlFor="email" className="text-sm font-semibold text-white/90">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="h-11 bg-white/5 border-white/10 focus:border-[hsl(262,83%,53%)] text-white placeholder:text-white/40 transition-colors"
                  />
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor="password" className="text-sm font-semibold text-white/90">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showLoginPassword ? "text" : "password"}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="h-11 bg-white/5 border-white/10 focus:border-[hsl(262,83%,53%)] text-white placeholder:text-white/40 transition-colors pr-10"
                    />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent text-white/50 hover:text-white transition-colors" 
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                    >
                      {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={() => setForgotPasswordOpen(true)}
                      className="text-sm font-medium text-[hsl(262,83%,53%)] hover:text-[hsl(262,83%,63%)] transition-colors hover:underline underline-offset-4"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 font-semibold landing-btn-primary border-0" 
                  disabled={loggingIn}
                >
                  {loggingIn ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
              <ForgotPasswordDialog 
                open={forgotPasswordOpen} 
                onOpenChange={setForgotPasswordOpen}
              />
            </TabsContent>
            
            <TabsContent value="signup" className="animate-fade-in">
              <form onSubmit={handleSignup} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2.5">
                    <Label htmlFor="firstName" className="text-sm font-semibold text-white/90">First Name</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="h-11 bg-white/5 border-white/10 focus:border-[hsl(262,83%,53%)] text-white placeholder:text-white/40 transition-colors"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor="lastName" className="text-sm font-semibold text-white/90">Last Name</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="h-11 bg-white/5 border-white/10 focus:border-[hsl(262,83%,53%)] text-white placeholder:text-white/40 transition-colors"
                    />
                  </div>
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor="signupEmail" className="text-sm font-semibold text-white/90">Email</Label>
                  <Input
                    id="signupEmail"
                    type="email"
                    placeholder="name@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                    className="h-11 bg-white/5 border-white/10 focus:border-[hsl(262,83%,53%)] text-white placeholder:text-white/40 transition-colors"
                    disabled={!!(inviteToken && invitedEmail)}
                  />
                  {inviteToken && invitedEmail && (
                    <p className="text-xs text-white/50 mt-1.5">
                      Email is locked to the invitation address
                    </p>
                  )}
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor="signupPassword" className="text-sm font-semibold text-white/90">Password</Label>
                  <div className="relative">
                    <Input
                      id="signupPassword"
                      type={showSignupPassword ? "text" : "password"}
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      required
                      className="h-11 bg-white/5 border-white/10 focus:border-[hsl(262,83%,53%)] text-white placeholder:text-white/40 transition-colors pr-10"
                    />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent text-white/50 hover:text-white transition-colors" 
                      onClick={toggleSignupPasswordVisibility}
                    >
                      {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold text-white/90">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showSignupPasswordConfirm ? "text" : "password"}
                      value={signupPasswordConfirm}
                      onChange={(e) => setSignupPasswordConfirm(e.target.value)}
                      required
                      className="h-11 bg-white/5 border-white/10 focus:border-[hsl(262,83%,53%)] text-white placeholder:text-white/40 transition-colors pr-10"
                    />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent text-white/50 hover:text-white transition-colors" 
                      onClick={toggleSignupPasswordConfirmVisibility}
                    >
                      {showSignupPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 font-semibold landing-btn-primary border-0" 
                  disabled={signingUp}
                >
                  {signingUp ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="flex flex-col border-t border-white/10 pt-6 px-6">
          <p className="text-xs text-center text-white/50 leading-relaxed">
            By continuing, you agree to our{' '}
            <span className="text-white/80 font-medium hover:text-[hsl(262,83%,53%)] transition-colors cursor-pointer">Terms of Service</span>
            {' '}and{' '}
            <span className="text-white/80 font-medium hover:text-[hsl(262,83%,53%)] transition-colors cursor-pointer">Privacy Policy</span>.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
